import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, Shield, LogOut, Users, Loader2, Building2, ArrowRightLeft, BookOpen, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { AuthService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { isMasterUser } from '@/utils/master';
import { apiRequest } from '@/utils/api-request';

interface UserProfileDropdownProps {
    trigger?: React.ReactNode;
}

interface UserData {
    name: string;
    email: string;
    phone: string;
    role_name: string;
    team_name: string;
    is_super_admin: boolean;
    client_name: string;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ trigger }) => {
    const navigate = useNavigate();
    const { userData: apiResponse, loading } = useUser(); // Use shared context
    const [isOpen, setIsOpen] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [hasMultipleAccounts, setHasMultipleAccounts] = useState(false);
    const [isMaster, setIsMaster] = useState(false);
    const [allClients, setAllClients] = useState<any[]>([]);
    const [showClientSwitcher, setShowClientSwitcher] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Transform API data whenever it changes
    useEffect(() => {
        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            const apiData = apiResponse.data.data;
            // Transform API data to our format
            const transformedData: UserData = {
                name: apiData.userName,
                email: apiData.userEmail,
                phone: apiData.phone,
                role_name: apiData.role?.role_name || 'No Role',
                team_name: apiData.team?.team_name || 'No Team',
                is_super_admin: apiData.role?.role_name?.toLowerCase().includes('admin') || false,
                client_name: apiData.client?.client_name || 'No Organization'
            };
            setUserData(transformedData);
            console.log('User data loaded from context:', transformedData);

            // Check if user has multiple accounts using accounts_count from /me endpoint
            const accountsCount = apiData.accounts_count || 0;
            setHasMultipleAccounts(accountsCount > 1);
            console.log('Accounts count from /me endpoint:', accountsCount, 'Has multiple:', accountsCount > 1);
        }

        // Check if current user is master
        const masterStatus = isMasterUser();
        setIsMaster(masterStatus);
        if (masterStatus) {
            loadAllClients();
        }
    }, [apiResponse]);

    const loadAllClients = async (search?: string) => {
        setLoadingClients(true);
        try {
            const url = search ? `/api/master/clients?search=${encodeURIComponent(search)}` : '/api/master/clients';
            const response = await apiRequest(url);
            if (response.success && response.data) {
                setAllClients(response.data);
            }
        } catch (error) {
            console.error('Error loading clients:', error);
        } finally {
            setLoadingClients(false);
        }
    };

    // Debounced search handler
    const handleSearchChange = (value: string) => {
        setClientSearch(value);

        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set new timeout for debounced search
        const timeout = setTimeout(() => {
            loadAllClients(value);
        }, 300);

        setSearchTimeout(timeout);
    };

    const handleSwitchToClient = async (clientId: number) => {
        try {
            const response = await apiRequest('/api/master/switch-to-client', {
                method: 'POST',
                body: JSON.stringify({ client_id: clientId })
            });

            if (response.success && response.data) {
                // Update token
                AuthService.setToken(response.data.token, { expires: 7 });
                // Reload page to apply new context
                window.location.reload();
            }
        } catch (error) {
            console.error('Error switching client:', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);

            const logoutRedirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login';

            // Use the modern AuthService for comprehensive logout
            await AuthService.logout({
                clearLocalStorage: true,
                clearSessionStorage: true,
                clearIndexedDB: true,
                notifyBackend: true,
                redirectTo: logoutRedirectUrl
            });

            // The redirect will happen automatically from AuthService
        } catch (error) {
            console.error('Logout failed:', error);
            // Force redirect even on error
            const logoutRedirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login';
            window.location.href = logoutRedirectUrl;
        } finally {
            setIsLoggingOut(false);
        }
    };

    if (loading) {
        return (
            <div className="w-10 h-10 bg-gray-200 rounded-full border-2 border-white animate-pulse" />
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {trigger ? (
                <div onClick={() => setIsOpen(!isOpen)}>
                    {trigger}
                </div>
            ) : (
                <button
                    onClick={() => {
                        console.log('User icon clicked, current state:', isOpen, 'userData:', userData);
                        setIsOpen(!isOpen);
                    }}
                    className="w-10 h-10 bg-white rounded-full border-2 border-white flex items-center justify-center transition-all duration-200 cursor-pointer hover:bg-gray-100 hover:shadow-lg transform hover:scale-110"
                    type="button"
                >
                    <span className="text-brandColor font-bold text-lg">
                        {(userData?.client_name || userData?.name || 'U').charAt(0).toUpperCase()}
                    </span>
                </button>
            )}

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[99999]">
                    {/* Header */}
                    <div className="bg-brandColor p-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                                <span className="text-brandColor font-bold text-xl">
                                    {(userData?.client_name || userData?.name || 'U').charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg text-white">{userData?.name || 'Guest User'}</h3>
                                <p className="text-xs text-white/90">{userData?.role_name || 'No role assigned'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Current Account/Client Section */}
                    {userData?.client_name && (
                        <div className="bg-brandColor/10 border-b-2 border-brandColor/20 px-4 py-3">
                            <div className="flex items-center space-x-2.5">
                                <div className="w-8 h-8 bg-brandColor rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Building2 size={16} className="text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Account</p>
                                    <p className="text-sm font-semibold text-gray-900 truncate">{userData.client_name}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* User Information */}
                    <div className="p-4 space-y-3">
                        {userData ? (
                            <>
                                <div className="flex items-center space-x-3 text-gray-700">
                                    <Mail size={16} className="text-gray-400" />
                                    <span className="text-sm">{userData.email}</span>
                                </div>

                                {userData.phone && (
                                    <div className="flex items-center space-x-3 text-gray-700">
                                        <Phone size={16} className="text-gray-400" />
                                        <span className="text-sm">{userData.phone}</span>
                                    </div>
                                )}

                                <div className="flex items-center space-x-3 text-gray-700">
                                    <Users size={16} className="text-gray-400" />
                                    <span className="text-sm">{userData.team_name}</span>
                                </div>

                                <div className="flex items-center space-x-3 text-gray-700">
                                    <Shield size={16} className="text-gray-400" />
                                    <div className="flex-1">
                                        <span className="text-sm">{userData.role_name}</span>
                                        {userData.is_super_admin && (
                                            <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                                Admin
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-gray-500">No user data available</div>
                        )}
                    </div>


                    {/* Actions */}
                    <div className="border-t border-gray-200">
                        {/* Master: Switch to Any Client */}
                        {isMaster && (
                            <div className="border-b border-gray-200">
                                <button
                                    onClick={() => setShowClientSwitcher(!showClientSwitcher)}
                                    className="w-full px-4 py-3 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center justify-between transition-colors font-semibold"
                                >
                                    <div className="flex items-center space-x-2">
                                        <Building2 size={16} />
                                        <span>🔑 Switch to Client</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {allClients.length > 0 && (
                                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">
                                                {allClients.length}
                                            </span>
                                        )}
                                        {showClientSwitcher ? (
                                            <ChevronUp size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </div>
                                </button>

                                {showClientSwitcher && (
                                    <div className="bg-amber-50/50">
                                        {/* Search Bar */}
                                        <div className="px-4 py-2 border-b border-amber-200">
                                            <div className="relative">
                                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Search by name, ID, or area..."
                                                    value={clientSearch}
                                                    onChange={(e) => handleSearchChange(e.target.value)}
                                                    className="w-full pl-9 pr-3 py-2 text-sm text-gray-900 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white placeholder:text-gray-400"
                                                />
                                            </div>
                                        </div>
                                        {/* Client List */}
                                        <div className="max-h-64 overflow-y-auto">
                                            {loadingClients ? (
                                                <div className="px-6 py-4 text-center text-sm text-gray-500">
                                                    <Loader2 size={16} className="animate-spin inline mr-2" />
                                                    Searching...
                                                </div>
                                            ) : allClients.length === 0 ? (
                                                <div className="px-6 py-4 text-center text-sm text-gray-500">
                                                    {clientSearch ? (
                                                        'No matching clients found'
                                                    ) : (
                                                        <div className="space-y-1">
                                                            <Search size={20} className="mx-auto text-gray-400" />
                                                            <p>Search by school name or ID</p>
                                                            <p className="text-xs text-gray-400">Min 2 characters</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                allClients.map((client) => (
                                                    <button
                                                        key={client.client_id}
                                                        onClick={() => handleSwitchToClient(client.client_id)}
                                                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-amber-100 text-gray-700 transition-colors group border-b border-amber-100 last:border-b-0"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-medium group-hover:text-amber-900 truncate">
                                                                {client.client_name}
                                                            </span>
                                                            {client.client_active === 1 && (
                                                                <span className="text-xs text-green-600 ml-2">●</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                                                            {client.sd_org_id > 0 && (
                                                                <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                                                    ID: {client.sd_org_id}
                                                                </span>
                                                            )}
                                                            {client.area_name && (
                                                                <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                                    📍 {client.area_name}
                                                                </span>
                                                            )}
                                                            {client.city_name && (
                                                                <span className="text-gray-600">
                                                                    {client.city_name}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {hasMultipleAccounts && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/settings/accounts');
                                }}
                                className="w-full px-4 py-3 text-left text-sm text-brandColor hover:bg-brandColor/10 flex items-center space-x-2 transition-colors"
                            >
                                <ArrowRightLeft size={16} />
                                <span>Switch Account</span>
                            </button>
                        )}
                        <div className={`flex items-center ${hasMultipleAccounts ? 'border-t border-gray-100' : ''}`}>
                            <button
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                                className="flex-1 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoggingOut ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Signing out...</span>
                                    </>
                                ) : (
                                    <>
                                        <LogOut size={16} />
                                        <span>Sign out</span>
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/docs');
                                }}
                                className="px-4 py-3 text-sm text-brandColor hover:bg-brandColor/10 flex items-center space-x-2 transition-colors border-l border-gray-100"
                                title="Documentation"
                            >
                                <BookOpen size={16} />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfileDropdown;